import { dayNumber } from '@squats/shared';
import { formatMonthDay } from '../dates.ts';

export type Point = { date: string; value: number };

/**
 * A small line chart of values over dates (spaced by real time, not evenly), with an optional smoothed
 * line on top. Labels the first, last, lowest and highest values; full numbers are in the list beside it.
 */
export function TrendChart({ points, smooth, label, format }: { points: Point[]; smooth?: Point[]; label: string; format: (v: number) => string }) {
  if (points.length < 2) return null;
  const W = 320;
  const H = 120;
  const PAD = { l: 6, r: 6, t: 16, b: 20 };
  const xs = points.map((p) => dayNumber(p.date));
  const vs = [...points, ...(smooth ?? [])].map((p) => p.value);
  const [x0, x1] = [Math.min(...xs), Math.max(...xs)];
  let [v0, v1] = [Math.min(...vs), Math.max(...vs)];
  if (v0 === v1) [v0, v1] = [v0 - 1, v1 + 1];
  const x = (d: string) => PAD.l + ((dayNumber(d) - x0) / Math.max(1, x1 - x0)) * (W - PAD.l - PAD.r);
  const y = (v: number) => PAD.t + (1 - (v - v0) / (v1 - v0)) * (H - PAD.t - PAD.b);
  const path = (ps: Point[]) => ps.map((p, i) => `${i ? 'L' : 'M'}${x(p.date).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const hi = points.reduce((a, b) => (b.value > a.value ? b : a));
  const lo = points.reduce((a, b) => (b.value < a.value ? b : a));

  return (
    <svg
      className="trend"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`${label}: from ${format(points[0]!.value)} to ${format(points.at(-1)!.value)}, highest ${format(hi.value)}`}
    >
      <path d={path(points)} className={smooth ? 'trend-raw' : 'trend-line'} />
      {smooth && smooth.length > 1 && <path d={path(smooth)} className="trend-line" />}
      {points.map((p) => (
        <circle key={p.date} cx={x(p.date)} cy={y(p.value)} r={smooth ? 1.8 : 2.6} className="trend-dot" />
      ))}
      <text x={x(hi.date)} y={y(hi.value) - 5} textAnchor={x(hi.date) > W - 40 ? 'end' : x(hi.date) < 40 ? 'start' : 'middle'} className="trend-label">
        {format(hi.value)}
      </text>
      {lo !== hi && (
        <text x={x(lo.date)} y={y(lo.value) - 6} textAnchor={x(lo.date) > W - 40 ? 'end' : x(lo.date) < 40 ? 'start' : 'middle'} className="trend-label">
          {format(lo.value)}
        </text>
      )}
      <text x={PAD.l} y={H - 3} className="trend-axis">
        {formatMonthDay(points[0]!.date)}
      </text>
      <text x={W - PAD.r} y={H - 3} textAnchor="end" className="trend-axis">
        {formatMonthDay(points.at(-1)!.date)}
      </text>
    </svg>
  );
}
